import type { Tag } from '@customerio/hydra-ui-api';
import { isConflictError } from '@/services/api/errors';

/**
 * The tag a typed name asks for.
 *
 * Asked for in the order the page this replaces asks
 * (`app/services/tags.ts:10-30`): whatever is already in hand, then the
 * workspace list read again, then a new one. The picker offers to create any
 * name its options do not match, and those options are a list that can be a
 * minute old and that hides the tags already on the row — so a name the
 * workspace has is that tag, not a duplicate to be refused.
 *
 * The reads and the write are passed in rather than reached for, which keeps
 * the order testable without a component around it.
 */

export type TagForNameOptions = {
  /** The name, already trimmed: the endpoint trims before storing. */
  wanted: string;
  /** Every tag the caller can already name, from any source. */
  inHand: readonly Tag[];
  /** Reads the workspace's tags again, fresh. */
  reread: () => Promise<readonly Tag[] | undefined>;
  create: (name: string) => Promise<Tag>;
};

export type TagForName = {
  tag: Tag;
  /** Whether the caller learned of this tag here, and should remember it. */
  isNew: boolean;
};

/** The tag a workspace calls this, by exact name. */
export function byName(tags: readonly Tag[] | undefined, name: string): Tag | undefined {
  return (tags ?? []).find((tag) => tag.name === name);
}

export async function tagForName({ wanted, inHand, reread, create }: TagForNameOptions): Promise<TagForName> {
  const held = byName(inHand, wanted);
  if (held != null) {
    return { tag: held, isNew: false };
  }

  const fresh = byName(await reread(), wanted);
  if (fresh != null) {
    return { tag: fresh, isNew: true };
  }

  try {
    return { tag: await create(wanted), isNew: true };
  } catch (error) {
    // A conflict means somebody named it between that read and this write.
    // The tag exists now, which is what was asked for, so it is returned
    // rather than reported.
    const raced = isConflictError(error) ? byName(await reread(), wanted) : undefined;
    if (raced == null) {
      throw error;
    }
    return { tag: raced, isNew: true };
  }
}
